/**
 * The shape of a quality audit's body, as the quality-audit skill publishes it.
 *
 * The audit is not computed here and deliberately so: its verdicts are conclusions the skill draws
 * from reading the repository, and a second opinion calculated by Phill would disagree with the report
 * the client was already sent. The skill commits each quarter's audit to the product repository as
 * `quality/<YYYY-Qn>.json`, and this is only how the tab reads it back.
 *
 * The API stores the body opaquely, so these types are the render contract rather than a validated
 * schema: every field is optional-ish because a newer checklist may carry fields this build predates.
 */

/** How the audit grades a check. `na` and `unverified` are outcomes, not failures. */
export type AuditVerdict = 'met' | 'partial' | 'gap' | 'na' | 'unverified';

/** What a verdict means for the client once the check's floor is applied. The label the tab leads with. */
export type AuditStanding = 'meetsTarget' | 'atStandard' | 'improvementPlanned' | 'notAssessed' | 'notApplicable';

const STANDINGS: AuditStanding[] = ['meetsTarget', 'atStandard', 'improvementPlanned', 'notAssessed', 'notApplicable'];

export interface AuditEvidence {
  title: string | null;
  detail: string | null;
  /** A file path, command or query the finding can be traced back to. */
  reference: string | null;
}

/** One figure in a headline strip, coloured by the verdict of the check behind it. */
export interface AuditStat {
  value: string;
  label: string;
  verdict: AuditVerdict | null;
  standing?: AuditStanding | null;
}

/** A page the audit flagged as worth optimising, in the performance section. */
export interface AuditOpportunity {
  score: string;
  verdict: AuditVerdict | null;
  route: string | null;
  detail: string | null;
}

/** A headline strip: the check tally, delivery metrics, or performance metrics. */
export interface AuditSection {
  title: string;
  stats: AuditStat[];
  legends: string[];
  opportunities: AuditOpportunity[];
  /** Share per standing, for the proportion bar under the tally. */
  breakdown?: { standing: AuditStanding | null; label: string | null; count: number }[];
}

/**
 * One assessed check. The roadmap items are the same shape: they are the checks that fell below their
 * floor, repeated up front because they are the ones that have to be scheduled.
 */
export interface AuditCheck {
  slug: string | null;
  name: string | null;
  mandatory?: boolean;
  verdict: AuditVerdict | null;
  standing?: AuditStanding | null;
  /** The one-line remediation, shown before the check is expanded. */
  summary?: string | null;
  target: string | null;
  finding: string | null;
  evidence: AuditEvidence[];
  nextStep: string | null;
  /** Why a check could not be assessed. Present instead of a next step on `unverified`. */
  reason?: string | null;
  /** The lowest verdict the check is allowed to sit at. */
  floor?: string | null;
  issueUrl: string | null;
}

export interface AuditCategory {
  title: string | null;
  checks: AuditCheck[];
}

export type AuditChangeDirection = 'improved' | 'regressed' | 'added' | 'removed' | 'reassessed';

const DIRECTIONS: AuditChangeDirection[] = ['improved', 'regressed', 'added', 'removed', 'reassessed'];

const VERDICTS: AuditVerdict[] = ['met', 'partial', 'gap', 'na', 'unverified'];

/** One check's move since the compared quarter. A null side means the check was absent that quarter. */
export interface AuditCheckChange {
  slug: string;
  name: string | null;
  from: AuditVerdict | null;
  to: AuditVerdict | null;
  fromStanding: AuditStanding | null;
  toStanding: AuditStanding | null;
  direction: AuditChangeDirection;
}

/** What changed since the compared quarter, as the API computed it and translated it to standings. */
export interface AuditChanges {
  since: string;
  checklistChanged: boolean;
  previousChecklistVersion: string | null;
  checklistVersion: string | null;
  /** Only the standings whose count moved. */
  standings: { standing: AuditStanding; delta: number }[];
  /** Checks in this audit's tally, and how many of them changed result, and which way. */
  total: number;
  changed: number;
  better: number;
  worse: number;
  same: number;
  checks: AuditCheckChange[];
}

export interface QualityAuditBody {
  period: string | null;
  /** The header line: quarter, checklist version, who assessed it and when. */
  meta: string[];
  /** The audit's own closing note: what each verdict means, and what generated the report. */
  footer: string | null;
  sections: AuditSection[];
  roadmap: AuditCheck[];
  categories: AuditCategory[];
  changes: AuditChanges | null;
}

/** `2026-Q2` — how the audit names a quarter, in its title and its filenames. */
export const qualityPeriodLabel = (audit: { year: number; quarter: number }): string =>
  `${audit.year}-Q${audit.quarter}`;

const list = <T>(value: T[] | undefined | null): T[] => (Array.isArray(value) ? value : []);

/**
 * A value the tab renders as text.
 *
 * Every one of these is declared a string, but the API stores the body opaquely, so what a field
 * actually holds is whatever the skill published. React throws on an object child, which takes the
 * whole tab down over one malformed field — so anything that is not scalar is dropped rather than
 * rendered. A row missing its label beats an audit the client cannot open.
 */
const text = (value: unknown): string | null => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return null;
};

const textList = (value: unknown): string[] =>
  list(value as unknown[])
    .map(text)
    .filter((entry): entry is string => entry !== null);

const standing = (value: unknown): AuditStanding | null =>
  STANDINGS.includes(value as AuditStanding) ? (value as AuditStanding) : null;

const number = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

const verdict = (value: unknown): AuditVerdict | null =>
  VERDICTS.includes(value as AuditVerdict) ? (value as AuditVerdict) : null;

const normaliseCheckChange = (value: unknown): AuditCheckChange[] => {
  const change = (value ?? {}) as Partial<Record<keyof AuditCheckChange, unknown>>;
  const slug = text(change.slug);
  if (!slug || !DIRECTIONS.includes(change.direction as AuditChangeDirection)) return [];
  return [
    {
      slug,
      name: text(change.name),
      from: verdict(change.from),
      to: verdict(change.to),
      fromStanding: standing(change.fromStanding),
      toStanding: standing(change.toStanding),
      direction: change.direction as AuditChangeDirection,
    },
  ];
};

/** Absent or without a quarter to compare against means there is nothing to show, not an empty section. */
const normaliseChanges = (value: unknown): AuditChanges | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const changes = value as Partial<Record<keyof AuditChanges, unknown>>;
  const since = text(changes.since);
  if (!since) return null;
  return {
    since,
    checklistChanged: changes.checklistChanged === true,
    previousChecklistVersion: text(changes.previousChecklistVersion),
    checklistVersion: text(changes.checklistVersion),
    standings: list(changes.standings as { standing?: unknown; delta?: unknown }[]).flatMap(part => {
      const which = standing(part?.standing);
      const delta = number(part?.delta);
      return which && delta ? [{ standing: which, delta }] : [];
    }),
    total: number(changes.total),
    changed: number(changes.changed),
    better: number(changes.better),
    worse: number(changes.worse),
    same: number(changes.same),
    checks: list(changes.checks as unknown[])
      .flatMap(normaliseCheckChange)
      .filter((change, index, all) => all.findIndex(other => other.slug === change.slug) === index),
  };
};

const normaliseEvidence = (evidence: AuditEvidence): AuditEvidence => ({
  title: text(evidence?.title),
  detail: text(evidence?.detail),
  reference: text(evidence?.reference),
});

/** Every field a check is rendered from, so neither an absent list nor an object child reaches React. */
const normaliseCheck = (check: AuditCheck): AuditCheck => ({
  ...check,
  slug: text(check?.slug),
  name: text(check?.name),
  mandatory: !!check?.mandatory,
  verdict: text(check?.verdict) as AuditVerdict | null,
  standing: standing(check?.standing),
  summary: text(check?.summary),
  target: text(check?.target),
  finding: text(check?.finding),
  evidence: list(check?.evidence).map(normaliseEvidence),
  nextStep: text(check?.nextStep),
  reason: text(check?.reason),
  floor: text(check?.floor),
  issueUrl: text(check?.issueUrl),
});

/**
 * The body an audit row carries, in the shape the tab renders.
 *
 * The API stores it opaquely, so this normalises rather than parses: an older Phill reading a newer
 * checklist renders the parts it knows and leaves the rest out, which is better than refusing to show
 * an audit the client has already been sent.
 *
 * It guarantees the render contract at every depth — every declared list is a list, and every declared
 * string is a string. Both matter because the renderers read `.length` on the lists and pass the
 * strings to React as children: a missing list or an object where a label belongs crashes the tab
 * instead of omitting one row.
 */
export const auditBody = (audit?: Record<string, unknown>): QualityAuditBody | null => {
  if (!audit) return null;
  const body = audit as unknown as QualityAuditBody;
  return {
    period: text(body.period),
    meta: textList(body.meta),
    footer: text(body.footer),
    sections: list(body.sections).map(section => ({
      ...section,
      title: text(section?.title) ?? '',
      stats: list(section?.stats).map(stat => ({
        value: text(stat?.value) ?? '',
        label: text(stat?.label) ?? '',
        verdict: text(stat?.verdict) as AuditVerdict | null,
        standing: standing(stat?.standing),
      })),
      legends: textList(section?.legends),
      opportunities: list(section?.opportunities).map(opportunity => ({
        score: text(opportunity?.score) ?? '',
        verdict: text(opportunity?.verdict) as AuditVerdict | null,
        route: text(opportunity?.route),
        detail: text(opportunity?.detail),
      })),
      // Absent and empty differ here: the bar is only drawn when the audit gave one.
      breakdown: section?.breakdown
        ? list(section.breakdown).map(part => ({
            standing: standing(part?.standing),
            label: text(part?.label),
            count: number(part?.count),
          }))
        : undefined,
    })),
    roadmap: list(body.roadmap).map(normaliseCheck),
    categories: list(body.categories).map(category => ({
      ...category,
      title: text(category?.title),
      checks: list(category?.checks).map(normaliseCheck),
    })),
    changes: normaliseChanges(body.changes),
  };
};
